import Image from 'next/image'
import Link from 'next/link'
import { Box, Typography } from '@mui/material'

// Types
import type { BrandCardProps } from '@/types/components/molecules/customers'

// One business in the directory: logo (or initial), name, type and city, the person behind it and its links
const BrandCardMolecule = (props: BrandCardProps) => {
	// Props
	const { lng, brand, labels } = props

	// Variables
	const { slug, name, person, personTitle, type, city, logo, instagram, menu, summary } = brand
	const links = [
		{ text: labels.storyLink, href: `/${lng}/success-stories/${slug}`, external: false },
		...(menu ? [{ text: labels.menuLink, href: `/${lng}/b/${menu}`, external: false }] : []),
		...(instagram ? [{ text: labels.instagramLink, href: `https://instagram.com/${instagram.replace('@', '')}`, external: true }] : [])
	]

	return (
		<Box sx={{ height: 1, display: 'flex', flexDirection: 'column', p: 3, borderRadius: 5, bgcolor: 'common.white' }}>
			<Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
				<Box
					sx={{
						position: 'relative',
						flexShrink: 0,
						width: 64,
						height: 64,
						borderRadius: 4,
						overflow: 'hidden',
						display: 'grid',
						placeItems: 'center',
						bgcolor: 'primary.ultralight',
						color: 'primary.main',
						fontSize: 26,
						fontWeight: 800
					}}
				>
					{logo ? <Image src={logo} alt={name} fill sizes="64px" style={{ objectFit: 'cover' }} /> : name.trim().charAt(0)}
				</Box>

				<Box sx={{ minWidth: 0 }}>
					<Typography component="h3" sx={{ fontSize: 18, fontWeight: 700 }}>
						{name}
					</Typography>

					<Typography sx={{ mt: 0.25, fontSize: 13, color: 'text.secondary' }}>{[type, city].filter(Boolean).join(' · ')}</Typography>
				</Box>
			</Box>

			<Typography sx={{ mt: 2.5, fontSize: 15, fontWeight: 600 }}>{[person, personTitle].filter(Boolean).join('، ')}</Typography>

			<Typography sx={{ mt: 1, fontSize: 14, lineHeight: 1.9, color: 'text.secondary', flexGrow: 1 }}>{summary}</Typography>

			<Box sx={{ mt: 2.5, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
				{links.map(link => (
					<Box
						key={link.href}
						component={link.external ? 'a' : Link}
						href={link.href}
						{...(link.external && { target: '_blank', rel: 'noopener' })}
						sx={{
							px: 1.5,
							py: 0.75,
							borderRadius: 2,
							fontSize: 13,
							fontWeight: 600,
							color: 'primary.main',
							bgcolor: 'primary.ultralight',
							textDecoration: 'none',
							transition: 'background-color .2s, color .2s',
							'&:hover': { bgcolor: 'primary.main', color: 'primary.contrastText' }
						}}
					>
						{link.text}
					</Box>
				))}
			</Box>
		</Box>
	)
}

export default BrandCardMolecule
